import { queryOptions, useQueryClient } from '@tanstack/vue-query'
import type { NitroFetchOptions, NitroFetchRequest } from 'nitropack'
import { NOTIFICATION_SUBSCRIPTION_LOCAL_STORAGE_KEY } from '~/composables/utils/product-listing/card-container/constants'
import { useStorage } from '@vueuse/core'
import { updateLocalStorageNotificationSubscribedIds } from '~/utils/notification-subscription-helpers'
import { isEmpty } from 'lodash-es'
import type { NotificationSubscribedVariantIds } from '~/types/api/bff/web/notification-subscription/remind-me.types'

export const useGetRemindMeQuery = (
  userId: ComputedRef<string | undefined>,
  options?: NitroFetchOptions<NitroFetchRequest>
) => {
  const { $notificationSubscriptionListRepository } = useNuxtApp()
  const client = useQueryClient()

  return queryOptions<string[]>({
    queryKey: getRemindMeQueryKey(userId),
    queryFn: async () => {
      const resolvedUserId = unref(userId)
      if (!resolvedUserId) return []

      const storageKey = `${NOTIFICATION_SUBSCRIPTION_LOCAL_STORAGE_KEY}${resolvedUserId}`
      const storedData = useStorage<{ data: number[]; expiryTime: number }>(
        storageKey,
        { data: [], expiryTime: 0 }, // default value for typecasting to Javascript object from JSON
        localStorage
      )

      const storedValue = storedData.value

      // Use stored data if valid
      if (
        !isEmpty(storedValue?.data) ||
        (storedValue?.expiryTime ?? 0) > Date.now()
      ) {
        const subscribedVariantIds = storedValue
          ? storedValue.data.map(String)
          : []

        client.setQueryData(
          getRemindMeQueryKey(resolvedUserId),
          subscribedVariantIds
        )
        return subscribedVariantIds
      }

      const subscriptions: NotificationSubscribedVariantIds =
        await $notificationSubscriptionListRepository.index(unref(options))

      updateLocalStorageNotificationSubscribedIds(resolvedUserId, subscriptions)

      client.setQueryData(getRemindMeQueryKey(resolvedUserId), subscriptions)

      return subscriptions
    },
  })
}
